import {
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { fieldHelpDescriptionId } from '@/utils/fieldIds';

interface FieldHelpProps {
  fieldName: string;
  text: string;
}

const HelpRoot = styled.span`
  position: relative;
  display: inline-flex;
`;

const HelpButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  margin: 0;
  border: 0;
  border-radius: 50%;
  padding: 0;
  background: transparent;
  color: #757575;
  cursor: pointer;

  &:hover {
    color: #181818;
  }

  &:focus-visible {
    outline: 2px solid #333;
    outline-offset: -6px;
  }
`;

const HelpLiveRegion = styled.span`
  position: absolute;
  right: 0;
  bottom: calc(100% + 6px);
  z-index: 10;
`;

const HelpBubble = styled.span`
  display: block;
  width: max-content;
  max-width: 240px;
  box-shadow: 0 2px 8px rgb(0 0 0 / 15%);
  border: 1px solid rgb(24 24 24 / 15%);
  border-radius: 4px;
  padding: 8px 12px;
  background: #fff;
  color: #181818;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  text-align: left;
`;

// Always present for screen readers so the field's aria-describedby
// resolves even while the visual bubble is closed.
const HelpDescription = styled.span`
  position: absolute;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  width: 1px;
  height: 1px;
  white-space: nowrap;
`;

export const FieldHelp = ({
  fieldName,
  text,
}: FieldHelpProps) => {
  const [
    isPinned,
    setIsPinned,
  ] = useState(false);
  const [
    isHovered,
    setIsHovered,
  ] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  const isOpen = isPinned || isHovered;

  const handleToggle = () => {
    if (isOpen) {
      setIsPinned(false);
      setIsHovered(false);
    } else {
      setIsPinned(true);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: Event) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsPinned(false);
        setIsHovered(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsPinned(false);
        setIsHovered(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <HelpRoot
      className="muster-field-help"
      ref={containerRef}
    >
      <HelpButton
        type="button"
        aria-expanded={isOpen}
        aria-label={`More information about ${fieldName}`}
        onClick={handleToggle}
        onPointerEnter={event => event.pointerType === 'mouse' && setIsHovered(true)}
        onPointerLeave={event => event.pointerType === 'mouse' && setIsHovered(false)}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </HelpButton>
      <HelpLiveRegion role="status">
        {isOpen && <HelpBubble>{text}</HelpBubble>}
      </HelpLiveRegion>
      <HelpDescription id={fieldHelpDescriptionId(fieldName)}>
        {text}
      </HelpDescription>
    </HelpRoot>
  );
};
